import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Slider,
  Chip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  WalteroButton,
  WalteroCard,
  WalteroTypography
} from './waltero';
import billingService from '../services/billingService';
import {
  CheckCircle as CheckCircleIcon,
  CreditCard as CreditCardIcon,
  Calculate as CalculateIcon
} from '@mui/icons-material';

/**
 * Activate Subscription Modal
 *
 * Handles subscription activation with quantity selection and payment
 */
const ActivateSubscriptionModal = ({
  open,
  onClose,
  subscription,
  organizationId,
  onSuccess
}) => {
  const theme = useTheme();
  const [quantity, setQuantity] = useState(10);
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Common quantity options
  const quickQuantities = [10, 25, 50, 100, 250, 500, 1000];

  // Load payment methods when modal opens
  useEffect(() => {
    if (open && organizationId) {
      loadPaymentMethods();
    }
  }, [open, organizationId]);

  // Set default payment method
  useEffect(() => {
    if (paymentMethods.length > 0 && !paymentMethodId) {
      const defaultMethod = paymentMethods.find(m => m.isDefault) || paymentMethods[0];
      setPaymentMethodId(defaultMethod.id);
    }
  }, [paymentMethods]);

  const loadPaymentMethods = async () => {
    try {
      setLoadingPaymentMethods(true);
      const data = await billingService.getPaymentMethods(organizationId);
      setPaymentMethods(data.paymentMethods || []);
    } catch (err) {
      console.error('Error loading payment methods:', err);
      setError('Failed to load payment methods. Please add a payment method first.');
    } finally {
      setLoadingPaymentMethods(false);
    }
  };

  // Calculate pricing
  const calculatePricing = () => {
    if (!subscription) return {};

    const pricePerSensor = subscription.pricePerSensorPerMonth || 1;
    const monthlyTotal = quantity * pricePerSensor;
    const annualTotal = monthlyTotal * 12;

    return {
      pricePerSensor,
      monthlyTotal,
      annualTotal,
      nextBillingDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
    };
  };

  const pricing = calculatePricing();

  // Handle quantity change
  const handleQuantityChange = (event) => {
    const value = parseInt(event.target.value) || 0;
    if (value >= 1 && value <= 10000) {
      setQuantity(value);
    }
  };

  // Handle slider change
  const handleSliderChange = (event, newValue) => {
    setQuantity(newValue);
  };

  // Handle activation
  const handleActivate = async () => {
    if (!paymentMethodId) {
      setError('Please select a payment method');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      await billingService.activateSubscription(
        organizationId,
        subscription.subscriptionTypeId || subscription.id,
        quantity,
        paymentMethodId
      );
      onSuccess();
    } catch (err) {
      console.error('Error activating subscription:', err);
      setError(err.error || 'Failed to activate subscription');
    } finally {
      setProcessing(false);
    }
  };

  // Format payment method display
  const formatPaymentMethod = (method) => {
    return `•••• ${method.card?.last4} (${method.card?.brand})`;
  };

  if (!subscription) return null;

  return (
    <Dialog
      open={open}
      onClose={() => !processing && onClose()}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <CheckCircleIcon
            sx={{
              fontSize: 32,
              color: theme.palette.waltero.primary.coreBlue,
              mr: 2
            }}
          />
          <Box>
            <WalteroTypography variant="h5" fontWeight="bold">
              Activate Subscription
            </WalteroTypography>
            <Typography variant="body2" color="textSecondary">
              {subscription.name}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Subscription Details */}
        <WalteroCard sx={{ p: 2, mb: 3, backgroundColor: theme.palette.grey[50] }}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Subscription Plan
          </Typography>
          <Typography variant="h6">{subscription.name}</Typography>
          <Typography variant="body2" color="textSecondary">
            ${subscription.pricePerSensorPerMonth} per sensor per month • Annual billing
          </Typography>
        </WalteroCard>

        {/* Quantity Selection */}
        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Select Sensor Quantity
          </Typography>

          {/* Quick Selection Chips */}
          <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
            {quickQuantities.map((q) => (
              <Chip
                key={q}
                label={q}
                onClick={() => setQuantity(q)}
                color={quantity === q ? 'primary' : 'default'}
                variant={quantity === q ? 'filled' : 'outlined'}
              />
            ))}
          </Box>

          {/* Custom Quantity Input */}
          <TextField
            fullWidth
            type="number"
            label="Number of Sensors"
            value={quantity}
            onChange={handleQuantityChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalculateIcon />
                </InputAdornment>
              ),
              inputProps: {
                min: 1,
                max: 10000
              }
            }}
            helperText="Enter the number of sensors you want to monitor (1-10,000)"
          />

          {/* Quantity Slider for fine-tuning */}
          {quantity <= 200 && (
            <Box mt={2}>
              <Slider
                value={quantity}
                onChange={handleSliderChange}
                min={1}
                max={200}
                step={1}
                marks={[
                  { value: 1, label: '1' },
                  { value: 50, label: '50' },
                  { value: 100, label: '100' },
                  { value: 150, label: '150' },
                  { value: 200, label: '200' }
                ]}
                valueLabelDisplay="auto"
              />
            </Box>
          )}
        </Box>

        {/* Pricing Calculator */}
        <WalteroCard
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: theme.palette.waltero.secondary.backgroundApricot + '30',
            border: `1px solid ${theme.palette.waltero.primary.coreBlue}`
          }}
        >
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Pricing Breakdown
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="textSecondary">
                  ${pricing.pricePerSensor} × {quantity} sensors × 12 months
                </Typography>
                <Typography variant="h5" color="primary">
                  ${pricing.annualTotal?.toFixed(2)}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">
                Monthly equivalent
              </Typography>
              <Typography variant="body1">
                ${pricing.monthlyTotal?.toFixed(2)}/month
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">
                Next billing date
              </Typography>
              <Typography variant="body1">
                {pricing.nextBillingDate?.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}
              </Typography>
            </Grid>
          </Grid>
        </WalteroCard>

        {/* Payment Method Selection */}
        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Payment Method
          </Typography>

          {loadingPaymentMethods ? (
            <CircularProgress size={24} />
          ) : paymentMethods.length === 0 ? (
            <Alert severity="warning">
              No payment methods available. Please add a payment method first.
            </Alert>
          ) : (
            <FormControl fullWidth>
              <InputLabel>Select Payment Method</InputLabel>
              <Select
                value={paymentMethodId}
                onChange={(e) => setPaymentMethodId(e.target.value)}
                label="Select Payment Method"
                startAdornment={
                  <InputAdornment position="start">
                    <CreditCardIcon />
                  </InputAdornment>
                }
              >
                {paymentMethods.map((method) => (
                  <MenuItem key={method.id} value={method.id}>
                    <Box display="flex" alignItems="center" width="100%">
                      <Typography flex={1}>
                        {formatPaymentMethod(method)}
                      </Typography>
                      {method.isDefault && (
                        <Chip label="Default" size="small" color="primary" />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        {/* Terms Notice */}
        <Alert severity="info" icon={false}>
          <Typography variant="body2">
            By activating this subscription, you agree to be charged{' '}
            <strong>${pricing.annualTotal?.toFixed(2)}</strong> annually for{' '}
            <strong>{quantity} sensors</strong>. You can adjust the quantity or cancel
            at any time. Increases are charged immediately, decreases take effect at
            the next billing period.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <WalteroButton
          onClick={onClose}
          disabled={processing}
        >
          CANCEL
        </WalteroButton>
        <WalteroButton
          variant="contained"
          onClick={handleActivate}
          disabled={processing || !paymentMethodId || paymentMethods.length === 0}
          startIcon={processing ? <CircularProgress size={16} /> : <CheckCircleIcon />}
        >
          {processing
            ? 'ACTIVATING...'
            : `ACTIVATE FOR $${pricing.annualTotal?.toFixed(2)}/YEAR`}
        </WalteroButton>
      </DialogActions>
    </Dialog>
  );
};

export default ActivateSubscriptionModal;